/**
 * ResponsiveActionBar — a row of actions that adapts to viewport width.
 *
 * Each action declares a `placement`:
 *   - 'inline'   (default): renders as a button on desktop; collapses INTO the
 *                 single overflow dropdown below `inlineFrom`.
 *   - 'dropdown': always lives inside the overflow dropdown.
 *
 * There is only ever ONE dropdown trigger. On desktop the dropdown holds the
 * 'dropdown' actions (if any) and inline actions render as buttons to its left.
 * Below the breakpoint, inline actions move into that same dropdown — they do
 * NOT spawn a second one. This is the fix for the "every button group grows its
 * own kebab on mobile" problem.
 *
 * Submenus (`submenuActions`) are ALWAYS dropdown-only — they never render as
 * inline buttons. They appear before the flat dropdown items at both breakpoints.
 *
 * Example:
 *   <ResponsiveActionBar
 *     dropdownLabel='Record actions'
 *     actions={[
 *       { key: 'draft', label: 'Draft email', icon: Mail01, onClick: draft },     // inline
 *       { key: 'note', label: 'Add note', icon: File06, onClick: addNote },        // inline
 *       { key: 'delete', label: 'Delete', icon: Trash01, onClick: del,
 *         placement: 'dropdown', destructive: true },                              // always dropdown
 *     ]}
 *     submenuActions={[
 *       { key: 'move-stage', label: 'Move stage', icon: ArrowRight,
 *         selectedKey: 'open',
 *         options: [{ key: 'open', label: 'Open' }, { key: 'won', label: 'Won' }],
 *         onSelect: key => moveStage(key) },
 *     ]}
 *   />
 *
 * Desktop: [Draft email] [Add note] [⋮ Move stage ▸ · Delete]
 * Mobile:  [⋮ Move stage ▸ · Draft email · Add note · Delete]
 */

import type { FC, ReactNode } from 'react';
import { Fragment, useCallback, useMemo } from 'react';

import { useBreakpoint } from '@/hooks/useBreakpoint';
import { cx } from '@/utils/cx';

import { Dropdown } from '../overlays/dropdown';
import { Tooltip } from '../tooltip/tooltip';
import type { ButtonProps } from './button';
import { Button } from './button';

export type ResponsiveActionPlacement = 'inline' | 'dropdown';

export interface ResponsiveAction {
  /** Stable unique key — used for React keys and dropdown item ids. */
  key: string;
  /** Visible label (button text / menu item text). */
  label: string;
  /** Leading icon component. */
  icon: FC<{ className?: string }>;
  /** Click / select handler. */
  onClick: () => void;
  /**
   * Where this action renders:
   *   'inline'   — button on desktop, collapses into the dropdown on mobile (default)
   *   'dropdown' — always inside the dropdown
   */
  placement?: ResponsiveActionPlacement;
  /** Disables the button / menu item. */
  isDisabled?: boolean;
  /** Renders with destructive (error) styling in the dropdown. */
  destructive?: boolean;
  /**
   * Optional helper tooltip, for actions whose consequence isn't obvious from
   * the label alone. On an enabled INLINE button it shows on hover; on a
   * disabled action (inline or dropdown) its `title` explains why the action
   * is unavailable, via the `disabledTooltip` affordance of Button /
   * Dropdown.Item — a natively disabled control never receives hover events,
   * so a plain wrapping tooltip would never appear.
   */
  tooltip?: { title: string; description?: string };
}

export interface ResponsiveSubmenuOption {
  /** Stable unique key — used for the React key, the RAC item id, and dispatch. */
  key: string;
  label: string;
  isDisabled?: boolean;
}

export interface ResponsiveSubmenuAction {
  /** Stable unique key. Lives in a SEPARATE namespace from flat action keys. */
  key: string;
  label: string;
  icon: FC<{ className?: string }>;
  options: ResponsiveSubmenuOption[];
  /** Option key rendered with a checkmark. */
  selectedKey?: string | null;
  onSelect: (key: string) => void;
}

export interface ResponsiveActionBarProps {
  /** The actions to render. Order is preserved within inline and dropdown groups. */
  actions: ResponsiveAction[];
  /**
   * Submenus rendered in the overflow dropdown — ALWAYS dropdown-only (they can
   * never be an inline button). They appear before the flat dropdown items.
   */
  submenuActions?: ResponsiveSubmenuAction[];
  /**
   * Tailwind breakpoint at/above which inline actions render as buttons.
   * Below it, they collapse into the dropdown. Default 'md' (768px).
   */
  inlineFrom?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  /** Accessible label for the overflow dropdown trigger. */
  dropdownLabel?: string;
  /** Placement for the dropdown popover. Default 'bottom end'. */
  dropdownPlacement?: 'bottom end' | 'bottom start' | 'top end' | 'top start';
  /**
   * Inline button size. Default 'xs' — these buttons sit in a surface
   * header/toolbar whose height is set by its content (a dense surface per
   * VISUAL_STYLE_GUIDE.md § Which size, where). The ladder owns the box;
   * never re-shrink with padding/text overrides.
   */
  inlineButtonSize?: ButtonProps['size'];
  /** Inline button color. Default 'secondary'. */
  inlineButtonColor?: ButtonProps['color'];
  /** Extra className for the wrapper row. */
  className?: string;
  /** Override className for inline buttons. */
  inlineButtonClassName?: string;
  /** Override className for the dropdown trigger. */
  dropdownTriggerClassName?: string;
}

const DEFAULT_DROPDOWN_TRIGGER_CLASS =
  'p-2 hover:bg-tertiary rounded-lg transition duration-100 ease-linear cursor-pointer';
const DESTRUCTIVE_ITEM_CLASS = '[&_svg]:text-error-600 [&_span]:text-error-600';
const EMPTY_SUBMENU_ACTIONS: ResponsiveSubmenuAction[] = [];
const EMPTY_SELECTED_KEYS: Set<string> = new Set();

export function ResponsiveActionBar({
  actions,
  submenuActions = EMPTY_SUBMENU_ACTIONS,
  inlineFrom = 'md',
  dropdownLabel = 'More actions',
  dropdownPlacement = 'bottom end',
  inlineButtonSize = 'xs',
  inlineButtonColor = 'secondary',
  className,
  inlineButtonClassName,
  dropdownTriggerClassName,
}: ResponsiveActionBarProps) {
  // useBreakpoint re-renders on resize so the layout tracks the live viewport,
  // not just the first render.
  const isInline = useBreakpoint(inlineFrom);

  const inlineActions = useMemo(
    () => actions.filter(a => a.placement !== 'dropdown'),
    [actions]
  );
  const dropdownOnlyActions = useMemo(
    () => actions.filter(a => a.placement === 'dropdown'),
    [actions]
  );

  // Desktop: dropdown holds only the always-dropdown actions.
  // Mobile: dropdown holds the collapsed inline actions first, then the
  // always-dropdown actions (so e.g. "Draft email, Add note" precede "Delete").
  const dropdownActions = useMemo(
    () =>
      isInline
        ? dropdownOnlyActions
        : [...inlineActions, ...dropdownOnlyActions],
    [isInline, inlineActions, dropdownOnlyActions]
  );

  const actionByKey = useMemo(
    () => Object.fromEntries(actions.map(a => [a.key, a])),
    [actions]
  );

  const handleAction = useCallback(
    (key: React.Key) => {
      actionByKey[String(key)]?.onClick();
    },
    [actionByKey]
  );

  const submenuRenderData = useMemo(
    () =>
      submenuActions.map(submenu => ({
        submenu,
        onAction: (key: React.Key) => submenu.onSelect(String(key)),
        selectedKeys: submenu.selectedKey
          ? new Set([submenu.selectedKey])
          : EMPTY_SELECTED_KEYS,
      })),
    [submenuActions]
  );

  if (actions.length === 0 && submenuActions.length === 0) {
    return null;
  }

  const inlineButtons: ReactNode = isInline
    ? inlineActions.map(action => {
        // A disabled Button never receives hover events, so wrapping it in
        // <Tooltip> would never show anything. Button's own `disabledTooltip`
        // renders a still-focusable trigger for exactly this case.
        const disabledTooltip =
          action.isDisabled && action.tooltip
            ? action.tooltip.title
            : undefined;
        const button = (
          <Button
            color={inlineButtonColor}
            size={inlineButtonSize}
            iconLeading={action.icon}
            onClick={action.onClick}
            isDisabled={action.isDisabled}
            disabledTooltip={disabledTooltip}
            className={inlineButtonClassName}
          >
            {action.label}
          </Button>
        );
        if (!action.tooltip || action.isDisabled) {
          return <Fragment key={action.key}>{button}</Fragment>;
        }
        return (
          <Tooltip
            key={action.key}
            title={action.tooltip.title}
            description={action.tooltip.description}
            placement='bottom'
          >
            {button}
          </Tooltip>
        );
      })
    : null;

  const dropdown: ReactNode =
    dropdownActions.length === 0 && submenuActions.length === 0 ? null : (
      <Dropdown.Root>
        <Dropdown.DotsButton
          aria-label={dropdownLabel}
          className={dropdownTriggerClassName ?? DEFAULT_DROPDOWN_TRIGGER_CLASS}
        />
        <Dropdown.Popover placement={dropdownPlacement}>
          {/*
            selectionMode='none' makes items behave as actions (role
            `menuitem`) rather than radio selections. onAction dispatches by
            key; the menu closes after each pick.
          */}
          <Dropdown.Menu selectionMode='none' onAction={handleAction}>
            {/*
              Submenus are dropdown-only at BOTH breakpoints — they never
              collapse into inline buttons. They render before the flat
              dropdown items so the order matches the full-page record menu.
              Submenu keys live in a SEPARATE namespace from flat action
              keys: SubmenuTrigger rows carry no `id`, so RAC assigns an
              auto-generated key that never collides with actionByKey, and
              onAction fires a silent no-op for them.
            */}
            {submenuRenderData.map(({ submenu, onAction, selectedKeys }) => (
              <Dropdown.SubmenuTrigger
                key={submenu.key}
                label={submenu.label}
                icon={submenu.icon}
              >
                <Dropdown.Popover>
                  <Dropdown.Menu
                    onAction={onAction}
                    selectedKeys={selectedKeys}
                  >
                    {submenu.options.map(option => (
                      <Dropdown.Item
                        key={option.key}
                        id={option.key}
                        label={option.label}
                        textValue={option.label}
                        isDisabled={option.isDisabled}
                      />
                    ))}
                  </Dropdown.Menu>
                </Dropdown.Popover>
              </Dropdown.SubmenuTrigger>
            ))}
            {dropdownActions.map(action => (
              <Dropdown.Item
                key={action.key}
                id={action.key}
                label={action.label}
                icon={action.icon}
                isDisabled={action.isDisabled}
                disabledTooltip={
                  action.isDisabled && action.tooltip
                    ? action.tooltip.title
                    : undefined
                }
                className={
                  action.destructive ? DESTRUCTIVE_ITEM_CLASS : undefined
                }
              />
            ))}
          </Dropdown.Menu>
        </Dropdown.Popover>
      </Dropdown.Root>
    );

  return (
    <div className={cx('flex items-center gap-1', className)}>
      {inlineButtons}
      {dropdown}
    </div>
  );
}
